import { useQuery } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse, MinimalCandidacy } from "@/types";

const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

export const candidaciesMinimalQueryKey = (
  companyId: string,
  jobId: string,
  options?: { includeTerminal: boolean },
) =>
  options
    ? (["candidacies-minimal", companyId, jobId, options] as const)
    : (["candidacies-minimal", companyId, jobId] as const);

export function useGetCandidaciesMinimal(
  includeTerminal = false,
  enabled = true,
) {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();

  return useQuery({
    queryKey: candidaciesMinimalQueryKey(currentCompanyId, currentJobId, {
      includeTerminal,
    }),
    queryFn: async () => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/minimal`;
      const res = await guacHttpClient.get<
        MinimalCandidacy[],
        HttpResponse<MinimalCandidacy[]>
      >(path, { params: { includeTerminal } });
      // Drop malformed rows missing a status change; they have no pipeline
      // stage and would crash filtering/bucketing downstream.
      return res.data.filter(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (candidacy) => candidacy.lastCandidacyStatusChange,
      );
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
}
